/**
 * SECURITY ALERTS SERVICE
 * 
 * Sistema de alertas de seguridad para detectar y notificar
 * eventos sospechosos o críticos
 */

import { db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('SecurityAlerts');

export type AlertSeverity = 'low' | 'medium' | 'high' | 'critical';

export type AlertType =
  | 'brute_force_attempt'
  | 'new_device_login'
  | 'sensitive_data_access'
  | 'permission_change'
  | 'password_changed'
  | 'multiple_failed_logins'
  | 'session_limit_exceeded'
  | 'key_rotation_needed'
  | 'suspicious_activity';

export interface SecurityAlert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  userId?: string;
  details: Record<string, any>;
  timestamp: string;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  notificationSent: boolean;
}

class SecurityAlertsService {
  private readonly COLLECTION = 'security_alerts';

  /**
   * Crear una alerta de seguridad
   */
  async createAlert(
    type: AlertType,
    severity: AlertSeverity,
    details: Record<string, any>,
    userId?: string
  ): Promise<string> {
    try {
      const alert: Omit<SecurityAlert, 'id'> = {
        type,
        severity,
        userId,
        details,
        timestamp: new Date().toISOString(),
        resolved: false,
        notificationSent: false
      };

      const docRef = await db.collection(this.COLLECTION).add(alert);

      logger.warn('Security alert created', { 
        alertId: docRef.id, 
        type, 
        severity, 
        userId 
      });

      return docRef.id;
    } catch (error) {
      logger.error('Failed to create security alert', error as Error);
      throw error;
    }
  }

  /**
   * Obtener alertas no resueltas
   */
  async getUnresolvedAlerts(limit = 50): Promise<SecurityAlert[]> {
    try {
      const snapshot = await db.collection(this.COLLECTION)
        .where('resolved', '==', false)
        .orderBy('timestamp', 'desc')
        .limit(limit)
        .get();

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SecurityAlert[];
    } catch (error) {
      logger.error('Failed to get unresolved alerts', error as Error);
      return [];
    }
  }

  /**
   * Obtener alertas por severidad
   */
  async getAlertsBySeverity(
    severity: AlertSeverity,
    limit = 50
  ): Promise<SecurityAlert[]> {
    try {
      const snapshot = await db.collection(this.COLLECTION)
        .where('severity', '==', severity)
        .where('resolved', '==', false)
        .orderBy('timestamp', 'desc')
        .limit(limit)
        .get();

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SecurityAlert[];
    } catch (error) {
      logger.error('Failed to get alerts by severity', error as Error);
      return [];
    }
  }

  /**
   * Resolver una alerta
   */
  async resolveAlert(alertId: string, resolvedBy: string): Promise<void> {
    try {
      await db.collection(this.COLLECTION).doc(alertId).update({
        resolved: true,
        resolvedAt: new Date().toISOString(),
        resolvedBy
      });

      logger.info('Security alert resolved', { alertId, resolvedBy });
    } catch (error) {
      logger.error('Failed to resolve alert', error as Error);
      throw error;
    }
  }

  /**
   * Obtener alertas de un usuario
   */
  async getUserAlerts(userId: string, limit = 20): Promise<SecurityAlert[]> {
    try {
      const snapshot = await db.collection(this.COLLECTION)
        .where('userId', '==', userId)
        .orderBy('timestamp', 'desc')
        .limit(limit)
        .get();

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SecurityAlert[];
    } catch (error) {
      logger.error('Failed to get user alerts', error as Error);
      return [];
    }
  }

  // ============================================
  // MÉTODOS DE CONVENIENCIA PARA EVENTOS COMUNES
  // ============================================

  /**
   * Alerta: Nuevo dispositivo detectado
   */
  async onNewDeviceLogin(
    userId: string,
    deviceInfo: { browser: string; os: string; device: string }
  ): Promise<void> {
    await this.createAlert(
      'new_device_login',
      'medium',
      { deviceInfo },
      userId
    );
  }

  /**
   * Alerta: Intento de fuerza bruta
   */
  async onBruteForceAttempt(
    identifier: string,
    attempts: number
  ): Promise<void> {
    await this.createAlert(
      'brute_force_attempt',
      'high',
      { identifier, attempts }
    );
  }

  /**
   * Alerta: Acceso a datos sensibles
   */
  async onSensitiveDataAccess(
    userId: string,
    dataType: string,
    recordId: string
  ): Promise<void> {
    await this.createAlert(
      'sensitive_data_access',
      'low',
      { dataType, recordId },
      userId
    );
  }

  /**
   * Alerta: Cambio de contraseña
   */
  async onPasswordChanged(userId: string): Promise<void> {
    await this.createAlert(
      'password_changed',
      'medium',
      { action: 'password_change' },
      userId
    );
  }

  /**
   * Alerta: Límite de sesiones excedido
   */
  async onSessionLimitExceeded(
    userId: string,
    sessionCount: number
  ): Promise<void> {
    await this.createAlert(
      'session_limit_exceeded',
      'low',
      { sessionCount },
      userId
    );
  }

  /**
   * Alerta: Rotación de claves necesaria
   */
  async onKeyRotationNeeded(daysUntilExpiry: number): Promise<void> {
    const severity: AlertSeverity = daysUntilExpiry <= 7 ? 'critical' : 'high';
    await this.createAlert(
      'key_rotation_needed',
      severity,
      { daysUntilExpiry }
    );
  }

  /**
   * Contar alertas críticas no resueltas (para badge en UI)
   */
  async getCriticalAlertsCount(): Promise<number> {
    try {
      const snapshot = await db.collection(this.COLLECTION)
        .where('resolved', '==', false)
        .where('severity', 'in', ['critical', 'high'])
        .get();

      return snapshot.size;
    } catch (error) {
      logger.error('Failed to count critical alerts', error as Error);
      return 0;
    }
  }
}

export const securityAlertsService = new SecurityAlertsService();





